import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * **A barra do topo de uma tela** — a das telas que abrem em diálogo por cima da conversa
 * (resultados de hotel, carro e voo) e, a seguir, a do shell do web-chat. Uma estrutura só para as
 * duas, para o mesmo passo da busca não mudar de barra conforme a pessoa chega pelo menu ou pela
 * conversa:
 *
 *   — `ScreenBarStart`: a volta (← ou ✕) e, no desktop, a trilha.
 *   — `ScreenBarCenter`: a pill de contexto da busca, ou o título.
 *   — `ScreenBarEnd`: as ações do canto (a mala da viagem).
 *   — `ScreenBarRow`: a segunda linha, de borda a borda (a fila de chips do celular). Vazia, some.
 *
 * Três colunas de grade com as laterais no mesmo mínimo de 40px: o centro
 * fica no meio do espaço LIVRE, mesmo com um canto vazio. A coluna do meio é `1fr`, nunca `auto`,
 * e o `ScreenBarCenter` é bloco, não flex: a pill mede a própria largura por `container:
 * inline-size` e mediria zero numa caixa que encolhe ao conteúdo.
 *
 * A primeira linha tem 56px mais o recuo da status bar, com respiro de 28px (16px abaixo de
 * `wide`); a divisória fica embaixo da barra inteira, depois da segunda linha. `compact` é a barra
 * fina do celular: 44px e 8px de respiro, sem segunda linha. Alturas em px, a mesma unidade da reserva
 * que o shell faz no rolo (`--chat-topbar`). Fundo e divisória são do host quando ele os desenha de
 * outro jeito (o véu do shell): `className` vence.
 */
function ScreenBar({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'header'> & { size?: 'default' | 'compact' }) {
  return (
    <header
      data-slot="screen-bar"
      data-size={size}
      className={cn(
        'grid shrink-0 grid-cols-[minmax(40px,auto)_minmax(0,1fr)_minmax(40px,auto)] items-center gap-x-3 border-b border-border bg-background pt-[env(safe-area-inset-top,0px)]',
        size === 'compact'
          ? 'grid-rows-[44px_auto] px-2'
          : 'grid-rows-[56px_auto] px-7 max-wide:px-4',
        className,
      )}
      {...props}
    />
  );
}

/** A altura da barra sem a segunda linha — a primeira linha, a divisória e o recuo da status bar
 *  —, para quem desconta a barra de uma coluna fixa (`--hui-results-page-chrome`). */
const SCREEN_BAR_HEIGHT = 'calc(57px + env(safe-area-inset-top, 0px))';

function ScreenBarStart({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="screen-bar-start"
      className={cn(
        'col-start-1 row-start-1 flex min-w-0 items-center justify-start gap-3',
        className,
      )}
      {...props}
    />
  );
}

function ScreenBarCenter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="screen-bar-center"
      className={cn('col-start-2 row-start-1 min-w-0', className)}
      {...props}
    />
  );
}

function ScreenBarEnd({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="screen-bar-end"
      className={cn(
        'col-start-3 row-start-1 flex min-w-0 items-center justify-end gap-3.5',
        className,
      )}
      {...props}
    />
  );
}

/** A segunda linha sai do respiro da barra e vai de borda a borda. Costuma ser o alvo de um
 *  portal (`ref`), então a vazia some (`:empty`) em vez de reservar altura. */
function ScreenBarRow({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="screen-bar-row"
      className={cn(
        'col-span-full row-start-2 -mx-7 min-w-0 empty:hidden max-wide:-mx-4',
        className,
      )}
      {...props}
    />
  );
}

export {
  ScreenBar,
  ScreenBarCenter,
  ScreenBarEnd,
  ScreenBarRow,
  ScreenBarStart,
  SCREEN_BAR_HEIGHT,
};
